
import { useState, useEffect } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { ExpenseItemSelection } from "./ExpenseItemSelection";
import { format, subMonths } from "date-fns";
import { es } from "date-fns/locale";
import { useHouses } from "@/hooks/useHouses";
import { useHouseExpenses, Expense } from "@/hooks/useHouseExpenses";

interface PaymentCreationFormProps {
  onSubmit: (data: {
    houseId: string;
    description: string;
    expenseIds: string[];
    totalAmount: number;
  }) => void;
  isSubmitting: boolean;
}

export const PaymentCreationForm = ({ onSubmit, isSubmitting }: PaymentCreationFormProps) => {
  const { houses } = useHouses();
  const [selectedHouse, setSelectedHouse] = useState<string>("");
  const [selectedMonth, setSelectedMonth] = useState<Date>(new Date());
  const [description, setDescription] = useState<string>("");
  const [selectedExpenses, setSelectedExpenses] = useState<Set<string>>(new Set());

  const { data: expenses = [], isLoading: expensesLoading } = useHouseExpenses(selectedHouse, selectedMonth);

  // Generate last 12 months for selection
  const months = Array.from({ length: 12 }, (_, i) => {
    const date = subMonths(new Date(), i);
    return {
      value: format(date, 'yyyy-MM'),
      label: format(date, 'MMMM yyyy', { locale: es }),
      date
    };
  });

  // Update description when house or month changes
  useEffect(() => {
    if (selectedHouse && selectedMonth) {
      const selectedHouseName = houses.find(h => h.id.toString() === selectedHouse)?.name || '';
      // Capitalize the first letter of the month
      const monthName = format(selectedMonth, 'MMMM', { locale: es });
      const capitalizedMonth = monthName.charAt(0).toUpperCase() + monthName.slice(1);
      const year = format(selectedMonth, 'yyyy');
      setDescription(`Gastos ${selectedHouseName} | ${capitalizedMonth} ${year}`);
    }
  }, [selectedHouse, selectedMonth, houses]);

  const handleMonthChange = (value: string) => {
    const month = months.find(m => m.value === value)?.date;
    if (month) {
      setSelectedMonth(month);
    }
  };

  const handleExpenseToggle = (expenseId: string) => {
    const newSelectedExpenses = new Set(selectedExpenses);
    if (newSelectedExpenses.has(expenseId)) {
      newSelectedExpenses.delete(expenseId);
    } else {
      newSelectedExpenses.add(expenseId);
    }
    setSelectedExpenses(newSelectedExpenses);
  };

  const calculateTotalAmount = () => {
    return expenses
      .filter(expense => selectedExpenses.has(expense.id))
      .reduce((sum, expense) => sum + (expense.amount || 0), 0);
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    onSubmit({
      houseId: selectedHouse,
      description,
      expenseIds: Array.from(selectedExpenses),
      totalAmount: calculateTotalAmount()
    });
  };

  return (
    <form onSubmit={handleFormSubmit} className="space-y-6">
      <div className="space-y-4">
        <div>
          <Label htmlFor="house">Casa</Label>
          <Select
            value={selectedHouse}
            onValueChange={setSelectedHouse}
          >
            <SelectTrigger id="house" className="w-full bg-white">
              <SelectValue placeholder="Seleccionar casa" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              {houses.map(house => (
                <SelectItem key={house.id} value={house.id.toString()}>
                  {house.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <Label htmlFor="month">Mes</Label>
          <Select
            value={format(selectedMonth, 'yyyy-MM')}
            onValueChange={handleMonthChange}
          >
            <SelectTrigger id="month" className="w-full bg-white">
              <SelectValue placeholder="Seleccionar mes" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              {months.map(month => (
                <SelectItem key={month.value} value={month.value}>
                  {month.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <Label htmlFor="description">Descripción (opcional)</Label>
          <Input
            id="description"
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="Descripción del cobro"
          />
        </div>
      </div>
      
      {selectedHouse && !expensesLoading && (
        <div className="pt-4">
          <h3 className="text-lg font-medium mb-3">Gastos disponibles</h3>
          
          <ExpenseItemSelection 
            expenses={expenses}
            selectedExpenses={selectedExpenses}
            onExpenseToggle={handleExpenseToggle}
          />
          
          <div className="flex justify-between items-center mt-6 p-3 bg-muted rounded-md">
            <span className="font-medium">Total seleccionado:</span>
            <span className="text-xl font-bold">
              ${calculateTotalAmount().toLocaleString('es-CL')}
            </span>
          </div>
        </div>
      )}
      
      <div className="flex justify-end pt-6">
        <Button 
          type="submit" 
          disabled={isSubmitting || selectedExpenses.size === 0} 
          className="w-full md:w-auto"
        >
          {isSubmitting ? "Creando cobro..." : "Crear cobro"}
        </Button>
      </div>
    </form>
  );
};
